import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPinned, Navigation, Search, Share2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { APP_NAME } from '../lib/lieux';

const MODES = [
  { id: 'login', label: 'Connexion' },
  { id: 'register', label: 'Inscription' },
  { id: 'otp', label: 'Code email' },
];

export default function AuthPage() {
  const { login, register, loginWithOTP, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ nom: '', email: '', password: '', otp: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const executer = async (e, action) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await action(); } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const submitLogin = (e) => executer(e, async () => { await login(form.email, form.password); navigate('/dashboard'); });
  const submitRegister = (e) => executer(e, async () => { await register(form.nom, form.email, form.password); navigate('/dashboard'); });
  const sendOTP = (e) => executer(e, async () => {
    const res = await api.sendOTP(form.email);
    if (res.error) throw new Error(res.error);
    setMessage(`Code envoyé à ${form.email}`);
    setStep(2);
  });
  const verifyOTP = (e) => executer(e, async () => { await loginWithOTP(form.email, form.otp); navigate('/dashboard'); });

  const changerMode = (m) => { setMode(m); setStep(1); setError(''); setMessage(''); };

  return (
    <div className="auth">
      <div className="auth-presentation">
        <div className="logo logo-clair"><span className="logo-marque"><MapPinned size={20} /></span>{APP_NAME}</div>
        <h1>Votre carnet de lieux géolocalisés.</h1>
        <ul className="auth-points">
          <li><Search size={18} /> Retrouvez un lieu par son nom, un alias ou son code</li>
          <li><Navigation size={18} /> Lancez la navigation vers la position exacte</li>
          <li><Share2 size={18} /> Partagez un lieu sur WhatsApp ou par QR Code</li>
        </ul>
      </div>

      <div className="auth-carte">
        <h2>{mode === 'register' ? 'Créer un compte' : 'Se connecter'}</h2>

        <div className="segments" role="tablist">
          {MODES.map(m => (
            <button key={m.id} role="tab" aria-selected={mode === m.id} className={mode === m.id ? 'actif' : ''} onClick={() => changerMode(m.id)}>
              {m.label}
            </button>
          ))}
        </div>

        {error && <p className="erreur" role="alert">{error}</p>}
        {message && <p className="succes">{message}</p>}

        {mode === 'login' && (
          <form onSubmit={submitLogin} className="form">
            <label className="champ"><span>Email</span>
              <input name="email" type="email" autoComplete="email" value={form.email} onChange={handle} required /></label>
            <label className="champ"><span>Mot de passe</span>
              <input name="password" type="password" autoComplete="current-password" value={form.password} onChange={handle} required /></label>
            <button type="submit" className="btn btn-primaire btn-large" disabled={loading}>{loading ? 'Connexion…' : 'Se connecter'}</button>
          </form>
        )}

        {mode === 'register' && (
          <form onSubmit={submitRegister} className="form">
            <label className="champ"><span>Nom complet</span>
              <input name="nom" autoComplete="name" value={form.nom} onChange={handle} required /></label>
            <label className="champ"><span>Email</span>
              <input name="email" type="email" autoComplete="email" value={form.email} onChange={handle} required /></label>
            <label className="champ"><span>Mot de passe</span>
              <input name="password" type="password" autoComplete="new-password" minLength={6} value={form.password} onChange={handle} required /></label>
            <button type="submit" className="btn btn-primaire btn-large" disabled={loading}>{loading ? 'Inscription…' : "Créer mon compte"}</button>
          </form>
        )}

        {mode === 'otp' && step === 1 && (
          <form onSubmit={sendOTP} className="form">
            <label className="champ"><span>Email</span>
              <input name="email" type="email" autoComplete="email" value={form.email} onChange={handle} required /></label>
            <button type="submit" className="btn btn-primaire btn-large" disabled={loading}>{loading ? 'Envoi…' : 'Recevoir un code'}</button>
          </form>
        )}

        {mode === 'otp' && step === 2 && (
          <form onSubmit={verifyOTP} className="form">
            <label className="champ"><span>Code à 6 chiffres</span>
              <input name="otp" inputMode="numeric" autoComplete="one-time-code" className="otp" value={form.otp} onChange={handle} required maxLength={6} /></label>
            <button type="submit" className="btn btn-primaire btn-large" disabled={loading}>{loading ? 'Vérification…' : 'Valider'}</button>
            <button type="button" className="btn btn-fantome" onClick={() => setStep(1)}>Changer d'email</button>
          </form>
        )}

        <div className="separateur"><span>ou</span></div>
        <button className="btn btn-secondaire btn-large btn-google" onClick={loginWithGoogle}>
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
          Continuer avec Google
        </button>
      </div>
    </div>
  );
}
